import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Select,
  Button,
  FormControl,
  FormLabel,
  NumberInputStepper,
  NumberInput,
  NumberInputField,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  Flex,
  Switch,
  Tooltip,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { getScheduledJob, putScheduledJob, setScheduledJobIsActive } from "services/ScheduleServices";
import cronstrue from "cronstrue";
import showToast from "hooks/useCustomToast";
import { ScheduledJobProto, ScheduledJobType, ScheduledPlanProto, ScheduledAgentProto } from "build-ts/schedule";
import { MetadataTypeEnum } from "build-ts/enums";
import { useUser } from "context/UserContext";

type ScheduleType = "minutes" | "hour" | "day" | "week" | "month";

interface ScheduledJobProps {
  jobHeader?: Header;
}

const ScheduledJob: React.FC<ScheduledJobProps> = ({ jobHeader }) => {
  const [scheduledJob, setScheduledJob] = useState<ScheduledJobProto | null>(null);
  const [scheduleType, setScheduleType] = useState<ScheduleType>("minutes");
  const [intervalValue, setIntervalValue] = useState<number>(30);
  const [hourValue, setHourValue] = useState<number>(6);
  const [timeHour, setTimeHour] = useState<number>(9);
  const [timeMinute, setTimeMinute] = useState<number>(0);
  const [timeSecond, setTimeSecond] = useState<number>(0);
  const [selectedWeekdays, setSelectedWeekdays] = useState<string[]>(["1"]);
  const [monthDay, setMonthDay] = useState<number>(1);
  const [cronExpression, setCronExpression] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isToggling, setIsToggling] = useState(false);

  const { defaultTimeZone } = useUser();

  const hasValidScheduledJob = scheduledJob?.header?.id;

  const weekdays = [
    { key: "1", label: "Mon", value: "MON" },
    { key: "2", label: "Tue", value: "TUE" },
    { key: "3", label: "Wed", value: "WED" },
    { key: "4", label: "Thu", value: "THU" },
    { key: "5", label: "Fri", value: "FRI" },
    { key: "6", label: "Sat", value: "SAT" },
    { key: "7", label: "Sun", value: "SUN" },
  ];

  useEffect(() => {
    loadScheduledJob();
  }, [jobHeader?.id]);

  useEffect(() => {
    if(scheduledJob){
      setCronExpression(scheduledJob?.cron_expr || "");
    }
  },[scheduledJob]);

  // useEffect to update cron expression when schedule type changes
  useEffect(() => {
    const cronExp = convertHumanReadableToCron(scheduleType, intervalValue, hourValue, timeHour, timeMinute, timeSecond, monthDay, selectedWeekdays);  
    setCronExpression(cronExp);
  },[scheduleType, intervalValue, hourValue, timeHour, timeMinute, timeSecond, monthDay, selectedWeekdays]);

  // function to load scheduled job
  const loadScheduledJob = async () => {
    if (!jobHeader?.id) return;

    setIsLoading(true);
    const job = await getScheduledJob(jobHeader?.id, false /* includeRuns */);
    setCronExpression(job?.cron_expr || "");
    setScheduledJob(job || null);
    setIsActive(job?.is_active || false);
    setIsLoading(false);
  };


  const handleWeekdayToggle = (weekday: string) => {
    if(scheduleType === "week"){
      // only allow 1 weekday to be selected (single select)
      setSelectedWeekdays([weekday]);
    }
    else if (scheduleType === "day"){
      // allow multiselect or no selection
      setSelectedWeekdays(prev => 
        prev.includes(weekday) 
          ? prev.filter(day => day !== weekday)
          : [...prev, weekday]
      );
    }
    else {
      // allow multiselect but at least one must remain selected (minutes, hour)
      setSelectedWeekdays(prev => {
        if (prev.includes(weekday)) {
          // If trying to unselect and it's the last selected weekday, prevent it
          if (prev.length === 1) {
            return prev; // Don't allow removing the last weekday
          }
          return prev.filter(day => day !== weekday);
        } else {
          return [...prev, weekday];
        }
      });
    }
  };

  const shouldShowWeekdays = () => {
    return ["minutes", "hour", "week", "day"].includes(scheduleType);
  };

  // function to convert cron expression to human readable string
  const getCronDescription = (cronExpr: string): string => {
    try {
      return cronstrue.toString(cronExpr);
    } catch (error) {
      return cronExpr; // Fallback to raw cron if parsing fails
    }
  };

  const handleChangeSchedule = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  // function to convert human readable string to cron expression
  const convertHumanReadableToCron = (ScheduleType: ScheduleType, intervalValue: number, hourValue: number, timeHour: number, timeMinute: number, timeSecond: number, monthDay: number, selectedWeekdays: string[]) => {
    switch(ScheduleType){
        case "minutes":
          return `0 ${intervalValue} * * * ${selectedWeekdays.join(",")}`;
        case "hour":
          return `0 0 ${hourValue} * * ${selectedWeekdays.join(",")}`;
        case "day":
          if(selectedWeekdays.length === 0){
            return `${timeSecond} ${timeMinute} ${timeHour} * * *`;
          }
          else{
            return `${timeSecond} ${timeMinute} ${timeHour} * * ${selectedWeekdays.join(",")}`;
          }
        case "week":
          return `0 ${timeMinute} ${timeHour} * * ${selectedWeekdays}`;
        case "month":
          return `0 ${timeMinute} ${timeHour} ${monthDay} * * *`;
    }
  };

  // Handle save schedule
  const handleSubmit = async () => {

    // Check if we have the necessary data
    if (!jobHeader) {
      showToast(
        "Missing job information",
        "Cannot schedule job without header",
        "error"
      );
      return;
    }

    const cronExpression = convertHumanReadableToCron(scheduleType, intervalValue, hourValue, timeHour, timeMinute, timeSecond, monthDay, selectedWeekdays);

    setIsLoading(true);
    // Create header for the scheduled job
    const scheduledJobHeader = Header.create({
      id: jobHeader?.id,
      name: scheduledJob?.header?.name || "Scheduled Job",
      description: `Scheduled execution of: ${jobHeader.name}`,
      type: MetadataTypeEnum.JOB,
    });
    let scheduledJobProto: ScheduledJobProto | undefined;
    let scheduledAgent: ScheduledAgentProto | undefined;
    let scheduledPlan: ScheduledPlanProto | undefined;
    
    if (jobHeader?.type === MetadataTypeEnum.CUSTOM_AGENT) {
      // Create scheduled QNA proto
      scheduledAgent = ScheduledAgentProto.create({
        agent_header: jobHeader,
      });
      scheduledJobProto = ScheduledJobProto.create({
        header: scheduledJobHeader,
        cron_expr: cronExpression.trim(),
        type: ScheduledJobType.QNA_EXECUTION,
        scheduled_agent: scheduledAgent,
        is_active: true,
      });
    } else {
      // Create scheduled plan proto
      scheduledPlan = ScheduledPlanProto.create({
        plan_header: jobHeader,
      });
      scheduledJobProto = ScheduledJobProto.create({
        header: scheduledJobHeader,
        cron_expr: cronExpression.trim(),
        type: ScheduledJobType.PLAN_EXECUTION,
        scheduled_plan: scheduledPlan,
        is_active: true,
      });
    }
    
    // Save the scheduled job
    const response = await putScheduledJob(scheduledJobProto);
    if (response) {
      showToast(
        scheduledJob?.header?.id
          ? "Schedule updated successfully"
          : "Job scheduled successfully",
        "",
        "success"
      );
      setScheduledJob(response);
      setIsActive(response?.is_active || false);
    } else {
      showToast("Failed to schedule job", "", "error");
    }
    setIsLoading(false);
    handleCancelEdit();  // Close the edit mode
  };
  
  const handleToggleJobStatus = async () => {
    if (!hasValidScheduledJob || !scheduledJob?.header?.id) return;

    const newActiveStatus = !isActive;
    setIsToggling(true);
    
    try {
      const result = await setScheduledJobIsActive(scheduledJob.header.id, newActiveStatus);

      if (result) {
        const action = newActiveStatus ? "activated" : "deactivated";
        showToast(
          `Schedule ${action} successfully`,
          `The scheduled job has been ${action}.`,
          "success"
        );
        setIsActive(newActiveStatus);
      } else {
        showToast("Failed to toggle schedule status", "", "error");
      }
    } catch (error) {
      console.error("Error toggling schedule status:", error);
      showToast("Failed to toggle schedule status", "", "error");
    } finally {
      setIsToggling(false);
    }
  };

  const renderScheduleOptions = () => {
    switch (scheduleType) {
      case "minutes":
        return (
          <HStack spacing={3} align="center">
            <Text fontSize="sm" pt={4}>Every</Text>
            <Select
              value={intervalValue}
              onChange={(e) => setIntervalValue(Number(e.target.value))}
              width="80px"
              size="sm"
            >
              {Array.from({ length: 60 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, "0")}
                </option>
              ))}
            </Select>
            <Text fontSize="sm">Minutes</Text>
            <Badge colorScheme="blue" fontSize="sm" mt={1} textAlign="left">
              {defaultTimeZone}
            </Badge>
          </HStack>
        );

      case "hour":
        return (
          <HStack spacing={3} align="center">
            <Text fontSize="sm" pt={4}>Every</Text>
            <Select
              value={hourValue}
              onChange={(e) => setHourValue(Number(e.target.value))}
              width="80px"
              size="sm"
            >
              {Array.from({ length: 24 }, (_, i) => i).map(val => (
                <option key={val} value={val}>{val}</option>
              ))}
            </Select>
            <Text fontSize="sm" pt={4}>Hours</Text>
            <Badge colorScheme="blue" fontSize="sm" mt={1} textAlign="left">
              {defaultTimeZone}
            </Badge>
          </HStack>
        );

      case "week":
      case "month":
        return (
          <HStack spacing={3} align="center">
            <Text fontSize="sm" pt={4}>at</Text>
            <Select
              value={timeHour}
              onChange={(e) => setTimeHour(Number(e.target.value))}
              width="70px"
              size="sm"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, "0")}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" pt={4}>:</Text>
            <Select
              value={timeMinute}
              onChange={(e) => setTimeMinute(Number(e.target.value))}
              width="70px"
              size="sm"
            >
              {Array.from({ length: 60 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, "0")}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" pt={4}>hours</Text>
            <Badge colorScheme="blue" fontSize="sm" mt={1} textAlign="left">
              {defaultTimeZone}
            </Badge>
          </HStack>
        );
      
      case "day":
        return (
          <HStack spacing={3} align="center">
            <Text fontSize="sm" pt={4}>at</Text>
            <Select
              value={timeHour}
              onChange={(e) => setTimeHour(Number(e.target.value))}
              width="70px"
              size="sm"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, "0")}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" pt={4}>:</Text>
            <Select
              value={timeMinute}
              onChange={(e) => setTimeMinute(Number(e.target.value))}
              width="70px"
              size="sm"
            >
              {Array.from({ length: 60 }, (_, i) => (
                <option key={i} value={i}>
                  {i.toString().padStart(2, "0")}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" pt={4}>:</Text>
            <Select
              value={timeSecond}
              onChange={(e) => setTimeSecond(Number(e.target.value))}
              width="70px"
              size="sm"
            >
              {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(val => (
                <option key={val} value={val}>
                  {val.toString().padStart(2, "0")}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" pt={4}>hours</Text>
            <Badge colorScheme="blue" fontSize="sm" mt={1} textAlign="left">
              {defaultTimeZone}
            </Badge>
          </HStack>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box p={4}>
        <Text fontSize="sm" color="gray.600">Loading schedule...</Text>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        {/* Header */}
        <Box textAlign="left">
          <Text fontSize="md" fontWeight="semibold" color="gray.800" mb={1}>
            Schedule Configuration
          </Text>
          {/* Status Toggle */}
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontSize="md" fontWeight="medium" color="gray.700" mb={0}>
                Current Status
            </Text>
            <Tooltip 
              label="Toggle to Activate or Deactivate" 
              placement="top"
              hasArrow
            >
              <Flex align="center" gap={3}>
                <Badge
                  colorScheme={isActive ? "green" : "red"}
                  variant="subtle"
                  px={2}
                  py={1}
                  borderRadius="md"
                  fontSize="sm"
                  >
                  {isActive ? "Active" : "Inactive"}
                </Badge>
                <Switch
                  isChecked={isActive}
                  onChange={handleToggleJobStatus}
                  isDisabled={isLoading || isToggling || !hasValidScheduledJob}
                  colorScheme="green"
                  size="md"
                  pt={2}
                />
              </Flex>
            </Tooltip>
          </Flex>
        </Box>

        {!isEditMode ? (
          // Read-only view showing current schedule
          <VStack spacing={3} align="stretch">
            {cronExpression ? (
              <Box textAlign="left">
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2} textAlign="left">
                  Current Schedule
                </Text>
                <Box p={3} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200" textAlign="left">
                  <Text fontSize="sm" color="gray.800" fontWeight="medium" textAlign="left">
                    {getCronDescription(cronExpression)}
                  </Text>
                  <Text fontSize="xs" color="gray.600" mt={1} fontFamily="mono" textAlign="left">
                    Cron: {cronExpression}
                  </Text>
                </Box>
                <Box textAlign="left">
                  <Button 
                    colorScheme="purple" 
                    size="sm" 
                    mt={3}
                    onClick={handleChangeSchedule}
                  >
                    Change Schedule
                  </Button>
                  <Box>
            </Box>
                </Box>
              </Box>
            ) : (
              <Box textAlign="left">
                <Text fontSize="sm" color="gray.600" mb={3} textAlign="left">
                  No schedule configured for this job.
                </Text>
                <Button 
                  colorScheme="purple" 
                  size="sm"
                  onClick={handleChangeSchedule}
                >
                  Create Schedule
                </Button>
              </Box>
            )}
          </VStack>
        ) : (
          // Edit mode with all form controls
          <VStack spacing={4} align="stretch">
            {/* Main Schedule Type Selector */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Send every
              </FormLabel>
              <HStack spacing={4} align="center">
                <Select
                  value={scheduleType}
                  onChange={(e) => {
                    setScheduleType(e.target.value as ScheduleType);
                    // Reset weekday selection when changing type
                    setSelectedWeekdays(["1"]);
                  }}
                  width="150px"
                  size="sm"
                  bg="white"
                  borderColor="gray.300"
                >
                  <option value="minutes">N minutes</option>
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </Select>
                
                {renderScheduleOptions()}
              </HStack>
            </FormControl>

            {/* Weekdays Selection */}
            {shouldShowWeekdays() && (
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                  Days of the week
                </FormLabel>
                <HStack spacing={2}>
                  {weekdays.map((day) => (
                    <Button
                      key={day.key}
                      size="sm"
                      variant={selectedWeekdays.includes(day.key) ? "solid" : "outline"}
                      colorScheme={selectedWeekdays.includes(day.key) ? "blue" : "gray"}
                      onClick={() => handleWeekdayToggle(day.key)}
                      minW="40px"
                      h="32px"
                      fontSize="xs"
                      fontWeight="medium"
                    >
                      {day.label}
                    </Button>
                  ))}
                </HStack>
                {scheduleType === "day"  || scheduleType === "minutes" || scheduleType === "hour" ? (
                  <Text fontSize="xs" color="gray.500" mt={1} textAlign="left">
                    Select multiple days for execution
                  </Text>
                ): (
                  <Text fontSize="xs" color="gray.500" mt={1} textAlign="left">
                    Select single day for execution
                  </Text>
                )}
              </FormControl>
            )}

            {/* Month Day Selection */}
            {scheduleType === "month" && (
              <FormControl textAlign="left">
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" textAlign="left">
                  Day of the month
                </FormLabel>
                <Box textAlign="left">
                  <HStack spacing={2}>
                    <NumberInput
                        size="xs"
                        value={monthDay}
                        min={1}
                        max={31}
                        onChange={(_, valueAsNumber) => {
                            setMonthDay(valueAsNumber);
                        }}
                        width="60px"
                    >
                        <NumberInputField />
                        <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                </HStack>
                </Box>
                <Text fontSize="xs" color="gray.500" mt={1} textAlign="left">
                  Select a date between 1 and 31
                </Text>
              </FormControl>
            )}

            {/* Preview */}
            <Box p={3} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
              <Text fontSize="xs" color="blue.800" fontWeight="medium" mb={1} textAlign="left">
                Schedule Preview:
              </Text>
              <Text fontSize="xs" color="blue.700" textAlign="left">
                {getCronDescription(cronExpression)}
              </Text>
            </Box>

            {/* Action Buttons */}
            <Box textAlign="right" pt={2}>
              <HStack spacing={3} justify="flex-end">
                <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button colorScheme="purple" size="sm" onClick={handleSubmit}>
                  Save Schedule
                </Button>
              </HStack>
            </Box>
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default ScheduledJob;